import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
} from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { AgentSessionType } from "schemas/dashboard";
import { Map } from "schemas/functions";
import theme from "styles/theme";

import {
  TemplateContentType,
  TemplateMethod,
} from "features/Influencer/ContactList/schema";
import PickTemplateMethodForm from "features/Influencer/HomePage/CreateTemplateDialog/Steps/PickTemplateMethodForm";
import PickTemplateTypeForm from "features/Influencer/HomePage/CreateTemplateDialog/Steps/PickTemplateTypeForm";
import TemplateAdditionalInfoForm from "features/Influencer/HomePage/CreateTemplateDialog/Steps/TemplateAdditionalInfoForm";
import TemplatePromptForm from "features/Influencer/HomePage/CreateTemplateDialog/Steps/TemplatePromptForm";
import TemplateCreationLoading from "features/Influencer/Templates/SharedComponents/TemplateCreationLoading";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";

import EditTemplateDialog from "../../EditTemplate";
import { FormAnswer, TemplateCreatePage } from "../schema";

interface Props {
  handleClose: () => void;
}

const ConversationalTemplate = ({ handleClose }: Props) => {
  const { setCreateTemplateOnboardingDialogOpen } = useContext(
    OnboardingDialogsContext,
  );
  const {
    editTemplateDialogId,
    setEditTemplateDialogId,
    outreachTemplates,
    setTemplates,
  } = useContext(OutreachTemplatesContext);
  const { slideOutRight, slideInRight, slideOutLeft, slideInLeft, slideProps } =
    useContext(SlideAnimationContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    templates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
    fetchOutreachTemplates,
    setAskUserToEdit,
  } = useContext(OutreachTemplatesContext);
  const isUserFirstTemplate = templates?.filter((x) => x.id!)?.length === 0;
  const { addTemplateTaskCompleted } = useGamification();

  const hasExistingTemplates =
    outreachTemplates && outreachTemplates.length > 0;
  const { setErrorAlert } = useContext(AlertContext);

  const [form, setForm] = useState<FormAnswer>({
    templateContentType: undefined,
    userPrompt: undefined,
  });
  const [page, setTemplateCreatePage] = useState<TemplateCreatePage>(
    TemplateCreatePage.method,
  );
  const [createLoading, setCreateLoading] = useState(false);

  const handleNext = async (method?: TemplateMethod) => {
    await slideOutLeft();
    trackEvent("Create Template Dialog Form - Next Button Pressed", {
      page,
      method,
    });

    if (page === TemplateCreatePage.method) {
      if (method === TemplateMethod.from_scratch) {
        setTemplateCreatePage(TemplateCreatePage.import);
      } else if (method === TemplateMethod.use_bento_ai) {
        setTemplateCreatePage(TemplateCreatePage.questions);
      }
    }
    slideInLeft();
  };

  const onSuccess = async () => {
    await fetchOutreachTemplates();
    handleClose();
  };

  const handleBack = async () => {
    trackEvent("Create Template Dialog Form - Back Button Pressed", {
      page,
    });

    await slideOutRight();
    if (page === TemplateCreatePage.method) {
      handleClose();
      return;
    }
    if (
      page === TemplateCreatePage.import ||
      page === TemplateCreatePage.questions
    ) {
      setTemplateCreatePage(TemplateCreatePage.method);
    } else if (page === TemplateCreatePage.prompt) {
      setTemplateCreatePage(TemplateCreatePage.questions);
    } else if (page === TemplateCreatePage.additional_info) {
      setTemplateCreatePage(TemplateCreatePage.prompt);
    } else if (page === TemplateCreatePage.bentoAi) {
      // Go back to additional_info if user has existing templates, otherwise questions
      if (hasExistingTemplates) {
        setTemplateCreatePage(TemplateCreatePage.additional_info);
      } else {
        setTemplateCreatePage(TemplateCreatePage.questions);
      }
    }
    slideInRight();
  };

  const handleSubmitForm = async (formData: Map) => {
    await slideOutLeft();
    // If user has existing templates and selected Outreach, show prompt page first
    if (hasExistingTemplates) {
      setTemplateCreatePage(TemplateCreatePage.prompt);
      slideInLeft();
      return;
    }
    // Otherwise, go directly to bento AI

    await initAgentSession(formData);
  };

  const handlePromptSubmit = async () => {
    await slideOutLeft();
    trackEvent("Create Template Dialog Form - User Submitted Custom Prompt", {
      prompt: form.userPrompt,
    });
    setTemplateCreatePage(TemplateCreatePage.additional_info);
    slideInLeft();
  };

  const handleAdditionalInfoSubmit = async () => {
    await slideOutLeft();
    setAskUserToEdit(true);
    await initAgentSession(form);
  };

  const initAgentSession = async (formData: Map) => {
    if (formData?.additionalInfo) {
      formData.userPrompt =
        formData?.userPrompt +
        `Incorporate the following additional information into the template for me: ${formData.additionalInfo}`;
    }
    setCreateLoading(true);
    try {
      const { outreach, followup } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/init`,
        "POST",
        {},
        { ...formData, type: AgentSessionType.template },
      );
      if (outreach?.id) {
        setCurrentTemplateContentType(TemplateContentType.OUTREACH);
        setEditTemplateDialogId(outreach.id);
        setTemplates((prev) => [outreach, ...(prev || [])]);
        addTemplateTaskCompleted(outreach?.contentType);
      } else if (followup?.id) {
        setCurrentTemplateContentType(TemplateContentType.FOLLOWUP);
        setEditTemplateDialogId(followup.id);
        setTemplates((prev) => [followup, ...(prev || [])]);
        addTemplateTaskCompleted(followup?.contentType);
      }
      setTemplateCreatePage(TemplateCreatePage.edit);
      slideInLeft();
      handleClose();
      if (outreach?.agentSessionId) {
        storeInfoFromAgentSession(outreach?.agentSessionId);
      } else {
        storeInfoFromAgentSession(followup?.agentSessionId);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const storeInfoFromAgentSession = async (agentSessionId: number) => {
    // Update OrganizationProfile info from the background from template content
    if (!agentSessionId) {
      return;
    }

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/agent-sessions/${agentSessionId}/store-portfolio`,
        "POST",
      );
    } catch (error) {
      // Do nothing
    }
  };

  useEffect(() => {
    slideInLeft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isUserFirstTemplate) {
    setCreateTemplateDialogOpen(false);
    setCreateTemplateOnboardingDialogOpen(true);
    return <></>;
  }

  if (createLoading) {
    return <TemplateCreationLoading creatingTemplateLoading={createLoading} />;
  }

  return (
    <>
      <Slide
        in={slideProps.in}
        direction={slideProps.direction}
        easing={{
          enter: theme.transitions.easing.easeInOut,
          exit: theme.transitions.easing.easeIn,
        }}
        timeout={{
          enter: ENTER_DELAY,
          exit: EXIT_DELAY,
        }}
      >
        <Grid item xs={12}>
          {page === TemplateCreatePage.method && (
            <>
              <Typography variant="h4">Create New Template</Typography>
              <PickTemplateMethodForm handleNext={handleNext} />
            </>
          )}
          {page === TemplateCreatePage.import && (
            <EditTemplateDialog
              isNew
              onSuccess={onSuccess}
              propsHandleClose={handleBack}
            />
          )}
          {page === TemplateCreatePage.questions && (
            <PickTemplateTypeForm
              handleSubmitForm={handleSubmitForm}
              handleBack={handleBack}
              form={form}
              setForm={setForm}
            />
          )}
          {page === TemplateCreatePage.prompt && (
            <TemplatePromptForm
              handleSubmit={handlePromptSubmit}
              handleBack={handleBack}
              form={form}
              setForm={setForm}
            />
          )}
          {page === TemplateCreatePage.additional_info && (
            <TemplateAdditionalInfoForm
              handleSubmit={handleAdditionalInfoSubmit}
              handleBack={handleBack}
              form={form}
              setForm={setForm}
            />
          )}
          {page === TemplateCreatePage.edit && editTemplateDialogId && (
            <EditTemplateDialog
              propsTemplateId={editTemplateDialogId}
              propsHandleClose={() => {
                setEditTemplateDialogId(null);
              }}
            />
          )}
        </Grid>
      </Slide>
    </>
  );
};

export default ConversationalTemplate;
